import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { I18nProvider } from "@/context/I18nContext";
import { ChatProvider } from "@/context/ChatContext";
import { SupportProvider } from "@/context/SupportContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";

import Home from "@/pages/Home";
import Products from "@/pages/Products";
import MobileCategory from "@/pages/MobileCategory";
import Zoomies from "@/pages/Zoomies";
import ProductDetail from "@/pages/ProductDetail";
import StorePage from "@/pages/StorePage";
import Cart from "@/pages/Cart";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyOtp from "@/pages/VerifyOtp";
import AuthCallback from "@/pages/AuthCallback";
import GoogleCallback from "@/pages/GoogleCallback";
import Wishlist from "@/pages/Wishlist";
import Following from "@/pages/Following";
import Partners from "@/pages/Partners";
import Account from "@/pages/Account";
import SellerApply from "@/pages/SellerApply";
import SellerCenter from "@/pages/SellerCenter";
import SellerDashboard from "@/pages/SellerDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import DeliveryDashboard from "@/pages/DeliveryDashboard";
import Settings from "@/pages/Settings";
import BlogHome from "@/pages/BlogHome";
import BlogPost from "@/pages/BlogPost";
import About from "@/pages/About";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Contact from "@/pages/Contact";
import CustomerService from "@/pages/CustomerService";
import DisputesReports from "@/pages/DisputesReports";
import ReturnRefundPolicy from "@/pages/ReturnRefundPolicy";
import ReturnPolicy from "@/pages/ReturnPolicy";
import ReportIPViolation from "@/pages/ReportIPViolation";
import DSAInformation from "@/pages/DSAInformation";
import IntegrityCompliance from "@/pages/IntegrityCompliance";
import TransparencyCenter from "@/pages/TransparencyCenter";
import OfflineComplaints from "@/pages/OfflineComplaints";
import { CookieConsent } from "@/components/CookieConsent";

function AppContent() {
  const location = useLocation();
  // Capture affiliate/influencer referral code from the URL so it survives to registration.
  useEffect(() => {
    const ref = new URLSearchParams(location.search).get("ref");
    if (ref) localStorage.setItem("xurray_ref", ref);
  }, [location.search]);
  // Handle Google OAuth return (session_id in URL fragment) before any route/ProtectedRoute runs.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/products" element={<Products />} />
      <Route path="/category" element={<MobileCategory />} />
      <Route path="/zoomies" element={<Zoomies />} />
      <Route path="/product/:id" element={<ProductDetail />} />
      <Route path="/store/:sellerId" element={<StorePage />} />
      <Route path="/cart" element={<Cart />} />
      <Route path="/checkout/success" element={<CheckoutSuccess />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/google" element={<GoogleCallback />} />
      <Route path="/register" element={<Login mode="register" />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify" element={<VerifyOtp />} />
      <Route path="/blog" element={<BlogHome />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/about" element={<About />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/customer-service" element={<CustomerService />} />
      <Route path="/disputes" element={<DisputesReports />} />
      <Route path="/return-refund-policy" element={<ReturnRefundPolicy />} />
      <Route path="/return-policy" element={<ReturnPolicy />} />
      <Route path="/report-ip-violation" element={<ReportIPViolation />} />
      <Route path="/dsa-osa" element={<DSAInformation />} />
      <Route path="/integrity-compliance" element={<IntegrityCompliance />} />
      <Route path="/transparency" element={<TransparencyCenter />} />
      <Route path="/offline-complaints" element={<OfflineComplaints />} />
      <Route path="/wishlist" element={<ProtectedRoute><Wishlist /></ProtectedRoute>} />
      <Route path="/following" element={<ProtectedRoute><Following /></ProtectedRoute>} />
      <Route path="/partners" element={<ProtectedRoute><Partners /></ProtectedRoute>} />
      <Route path="/account" element={<ProtectedRoute roles={["buyer", "seller", "admin"]}><Account /></ProtectedRoute>} />
      <Route path="/sell" element={<SellerCenter />} />
      <Route path="/seller/apply" element={<ProtectedRoute><SellerApply /></ProtectedRoute>} />
      <Route path="/seller" element={<ProtectedRoute roles={["seller", "admin"]}><SellerDashboard /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/delivery" element={<ProtectedRoute roles={["delivery", "admin"]}><DeliveryDashboard /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute roles={["buyer", "seller", "admin", "delivery"]}><Settings /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <I18nProvider>
            <CartProvider>
              <CurrencyProvider>
                <ChatProvider>
                  <SupportProvider>
                    <AppContent />
                    <Toaster position="top-right" richColors />
                    <CookieConsent />
                  </SupportProvider>
                </ChatProvider>
              </CurrencyProvider>
            </CartProvider>
          </I18nProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
